import React, { useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Stack,
  Button,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
  Typography,
  Chip,
} from "@mui/material";

import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  AUTOCOMPLETE_DROPDOWN_SLOT_PROPS,
  SELECT_MENU_PROPS,
} from "../../../theme/filterstyles";

import LeadScore from "./LeadScore";
import WebsiteVisitTags from "./WebsiteVisitTags";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const STATE_ENABLED_COUNTRIES = ["United States", "Canada"];
const ALL_TARGET_OPTION = "⭐ All Target Sectors";

const HEAD_CELL_SX = {
  backgroundColor: BRAND_BLUE,
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const DATA_CELL_SX = {
  fontSize: "0.875rem",
  color: "#344863",
  borderBottom: "1px solid #edf0f5",
  py: 0.875,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: BRAND_BLUE,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const FILTER_MENU_WIDTH = 380;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": { fontSize: "0.875rem", backgroundColor: "#ffffff" },
  "& .MuiInputBase-input": { py: 0.75, color: "#00336C", fontWeight: 700 },
  "& input": { color: "#00336C !important", WebkitTextFillColor: "#00336C", caretColor: "#00336C" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const PANEL_SX = {
  p: 1.25,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

const PANEL_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    fontSize: "0.92rem",
    color: "#00336C",
    borderRadius: 3,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0, 51, 108, 0.08)",
    "& fieldset": { border: "2px solid #00336C" },
    "&:hover fieldset": { borderColor: "#00336C" },
    "&.Mui-focused fieldset": { borderColor: "#00336C", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: "#00336C", fontWeight: 700 },
  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { color: "#00336C" },
};

const FILTER_CHIP_SX = {
  height: 22,
  backgroundColor: "#00336C",
  color: "white",
  fontWeight: 700,
  "& .MuiChip-label": { px: 0.75 },
  "& .MuiChip-deleteIcon": {
    color: "white",
    "&:hover": { opacity: 0.85 },
  },
};

function WebsiteVisitsTable({
  sortedRows,
  totalRowCount = sortedRows.length,
  selectedGroup,
  onSelectGroup,
  selectedIds,
  onToggleOne,
  onToggleAll,
  isAllSelected,
  isSomeSelected,
  leadScoreSort,
  onToggleLeadScoreSort,
  getGroupId,
  getStateDisplay,
  deriveCountry,
  getNaicsFields,
  getSector,
  cleanDisplay,
  displayOrDash,
  clientName,
  loading,
  err,
  normGuid,
  handleScroll,
  scrollRef,
  onVisitTagsUpdated,
  // sector filters
  targetIndustryFilter = [],
  onTargetIndustryChange,
  industryFilter = [],
  onIndustryChange,
  // naics filter
  naicsFilter = [],
  onNaicsChange,
  naicsOptions = [],
  // location filters
  countryFilter = [],
  onCountryChange,
  stateFilter = [],
  onStateChange,
  // filter options
  targetIndustryOptions = [],
  industryOptions = [],
  countryOptions = [],
  stateOptions = [],
}) {
  const hiddenRowCount = Math.max(totalRowCount - sortedRows.length, 0);

  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const openFilterMenu = useCallback((event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  }, []);

  const closeFilterMenu = useCallback(() => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  }, []);

  const renderTags = (value, getTagProps) => {
    if (!value.length) return null;
    const extra = value.length - 1;
    const first = value[0]?.label || value[0];
    return [
      <Chip
        {...getTagProps({ index: 0 })}
        key={String(first)}
        size="small"
        label={extra > 0 ? `${first} +${extra}` : first}
        sx={FILTER_CHIP_SX}
      />,
    ];
  };

  const renderFilterTrigger = (label, filterKey, active = false, minWidth = 120) => (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <Button
        variant="text"
        onClick={(event) => openFilterMenu(event, filterKey)}
        sx={{
          ...HEADER_TRIGGER_SX,
          minWidth,
          color: active ? BRAND_GREEN : BRAND_BLUE,
          border: active ? `1.5px solid ${BRAND_GREEN}` : HEADER_TRIGGER_SX.border,
          "&:hover": {
            backgroundColor: "#ffffff",
            borderColor: active ? BRAND_GREEN : "rgba(255,255,255,0.8)",
          },
        }}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
      >
        {label}
      </Button>
    </Stack>
  );

  const renderSearchField = (placeholder) => (
    <Box sx={SEARCH_FIELD_SX}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={filterSearch}
        onChange={(event) => setFilterSearch(event.target.value)}
        onKeyDown={(event) => event.stopPropagation()}
        placeholder={placeholder}
        sx={FILTER_SEARCH_INPUT_SX}
        slotProps={{
          input: {
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
          },
        }}
      />
    </Box>
  );

  const commonMenuProps = {
    open: Boolean(filterAnchor),
    anchorEl: filterAnchor,
    onClose: closeFilterMenu,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    marginThreshold: 16,
    MenuListProps: {
      dense: true,
      sx: {
        py: 0,
        maxHeight: 520,
        width: FILTER_MENU_WIDTH,
        maxWidth: FILTER_MENU_MAX_WIDTH,
        boxSizing: "border-box",
        overflowX: "hidden",
      },
    },
    ...SELECT_MENU_PROPS,
    PaperProps: {
      ...SELECT_MENU_PROPS.PaperProps,
      sx: {
        ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
        width: FILTER_MENU_WIDTH,
        maxWidth: FILTER_MENU_MAX_WIDTH,
        boxSizing: "border-box",
        maxHeight: 560,
        overflowX: "hidden",
        overflowY: "auto",
        py: 0,
      },
    },
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    if (activeFilter === "country") {
      const filtered = filterSearch
        ? countryOptions.filter((c) => c.toLowerCase().includes(filterSearch.toLowerCase()))
        : countryOptions;
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search countries")}
          <MenuItem
            selected={!countryFilter.length}
            onClick={() => { onCountryChange([]); onStateChange([]); }}
          >
            All Countries
          </MenuItem>
          {filtered.map((option) => (
            <MenuItem
              key={option}
              selected={countryFilter.includes(option)}
              onClick={() => {
                const next = countryFilter.includes(option)
                  ? countryFilter.filter((c) => c !== option)
                  : [...countryFilter, option];
                onCountryChange(next);
                if (!next.some((c) => STATE_ENABLED_COUNTRIES.includes(c))) {
                  onStateChange([]);
                }
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "state") {
      const isEnabled = countryFilter.some((c) => STATE_ENABLED_COUNTRIES.includes(c));
      const filtered = filterSearch
        ? stateOptions.filter((s) => s.toLowerCase().includes(filterSearch.toLowerCase()))
        : stateOptions;
      return (
        <Menu {...commonMenuProps}>
          {!isEnabled ? (
            <Box sx={{ p: 2, color: "rgba(0,0,0,0.45)", fontSize: "0.85rem" }}>
              Select United States or Canada first
            </Box>
          ) : (
            <>
              {renderSearchField("Search states / provinces")}
              <MenuItem selected={!stateFilter.length} onClick={() => onStateChange([])}>
                All States / Provinces
              </MenuItem>
              {filtered.map((option) => (
                <MenuItem
                  key={option}
                  selected={stateFilter.includes(option)}
                  onClick={() => {
                    const next = stateFilter.includes(option)
                      ? stateFilter.filter((s) => s !== option)
                      : [...stateFilter, option];
                    onStateChange(next);
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </>
          )}
        </Menu>
      );
    }

    if (activeFilter === "sector") {
      return (
        <Menu {...commonMenuProps}>
          <Box sx={PANEL_SX} onKeyDown={(e) => e.stopPropagation()}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ color: "#00336C", fontWeight: 800 }}>Sector Filters</Typography>
              <Button
                size="small"
                onClick={() => { onIndustryChange([]); onTargetIndustryChange([]); }}
                sx={{ color: "#00336C", fontWeight: 700, textTransform: "none" }}
              >
                Clear
              </Button>
            </Stack>

            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={industryOptions}
              value={industryFilter}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.label === val.label}
              onChange={(_e, values) => onIndustryChange(values ?? [])}
              slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
              sx={PANEL_FIELD_SX}
              renderTags={(value, getTagProps) => renderTags(value, getTagProps)}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sectors"
                  placeholder="Filter by sector"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={targetIndustryOptions}
              value={targetIndustryFilter}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.label === val.label}
              onChange={(_e, values) => {
                const next = values ?? [];
                const hasAll = next.some((v) => v.isAllTarget);
                if (hasAll) {
                  onTargetIndustryChange([{ label: ALL_TARGET_OPTION, isAllTarget: true }]);
                  return;
                }
                onTargetIndustryChange(next);
              }}
              slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
              sx={PANEL_FIELD_SX}
              renderTags={(value, getTagProps) => renderTags(value, getTagProps)}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Sectors"
                  placeholder="Filter by target sector"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )}
            />
          </Box>
        </Menu>
      );
    }

    if (activeFilter === "naics") {
      const filtered = filterSearch
        ? naicsOptions.filter((n) => n.toLowerCase().includes(filterSearch.toLowerCase()))
        : naicsOptions;
      return (
        <Menu {...commonMenuProps}>
          {renderSearchField("Search NAICS")}
          <MenuItem selected={!naicsFilter.length} onClick={() => onNaicsChange([])}>
            All NAICS
          </MenuItem>
          {filtered.map((option) => (
            <MenuItem
              key={option}
              selected={naicsFilter.includes(option)}
              onClick={() => {
                const next = naicsFilter.includes(option)
                  ? naicsFilter.filter((n) => n !== option)
                  : [...naicsFilter, option];
                onNaicsChange(next);
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  const sectorFilterActive = industryFilter.length > 0 || targetIndustryFilter.length > 0;

  return (
    <TableContainer
      component="div"
      sx={{
        border: `1.5px solid ${BRAND_BLUE}`,
        borderRadius: 2,
        boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
        background: "#ffffff",
        overflow: "hidden",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flexGrow: 1, overflowY: "auto", overflowX: "auto" }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: { xs: 1280, lg: 1400 }, borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center" sx={{ ...HEAD_CELL_SX, width: 60 }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={onToggleAll}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 260 }}>
                Company
              </TableCell>

              <TableCell
                align="center"
                onClick={onToggleLeadScoreSort}
                sx={{ ...HEAD_CELL_SX, width: 140, cursor: "pointer", userSelect: "none", "&:hover": { backgroundColor: "#002857" } }}
              >
                Lead Score{" "}
                {leadScoreSort === "asc" && <ArrowDropUpIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.25 }} />}
                {leadScoreSort === "desc" && <ArrowDropDownIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.25 }} />}
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 200 }}>
                Tags
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 180 }}>
                {renderFilterTrigger("State / Province", "state", stateFilter.length > 0, 158)}
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 180 }}>
                {renderFilterTrigger("Country", "country", countryFilter.length > 0, 130)}
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 240 }}>
                {renderFilterTrigger("Sector", "sector", sectorFilterActive, 130)}
              </TableCell>

              <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: 300 }}>
                {renderFilterTrigger("NAICS Text", "naics", naicsFilter.length > 0, 140)}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: "rgba(0,0,0,0.4)", fontSize: "0.875rem" }}>
                  Loading…
                </TableCell>
              </TableRow>
            )}

            {!loading && !err && sortedRows.length === 0 && normGuid && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: "rgba(0,0,0,0.35)", fontSize: "0.875rem", fontWeight: 500 }}>
                  No results
                </TableCell>
              </TableRow>
            )}

            {sortedRows.map((group) => {
              const row = group.representative;
              const idStr = getGroupId(group);
              const isChecked = idStr ? selectedIds.has(idStr) : false;

              const companyTitle =
                cleanDisplay(row.visitor_company_name) ||
                cleanDisplay(row.visitor_account_name);

              const country = deriveCountry(row);
              const state = getStateDisplay(row.visitor_state, row.visitor_country || country);
              const { naicsName } = getNaicsFields(row);
              const sectorLabel = group.sector || getSector(row);
              const selectedRow = selectedGroup && getGroupId(selectedGroup) === idStr;

              return (
                <TableRow
                  key={idStr}
                  hover
                  onClick={() => onSelectGroup(group)}
                  sx={{
                    backgroundColor: selectedRow ? "rgba(0,51,108,0.07)" : "#ffffff",
                    boxShadow: selectedRow ? "inset 3px 0 0 #00336C" : "none",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: selectedRow
                        ? "rgba(0,51,108,0.09) !important"
                        : "rgba(0,51,108,0.03) !important",
                    },
                  }}
                >
                  <TableCell padding="checkbox" align="center" sx={{ borderBottom: "1px solid #edf0f5" }}>
                    <Checkbox
                      disabled={!idStr}
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => idStr && onToggleOne(idStr)}
                      size="small"
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...DATA_CELL_SX,
                      fontWeight: 700,
                      color: BRAND_BLUE,
                      fontSize: "0.9rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 260,
                    }}
                    title={companyTitle || "—"}
                  >
                    {companyTitle || "—"}
                  </TableCell>

                  <TableCell align="center" sx={{ ...DATA_CELL_SX }}>
                    <LeadScore
                      score={group.leadScore}
                      visitorName={companyTitle || ""}
                      clientName={clientName}
                      size="small"
                    />
                  </TableCell>

                  <TableCell align="left" sx={{ ...DATA_CELL_SX }}>
                    <Stack alignItems="flex-start" sx={{ width: "100%" }}>
                      <WebsiteVisitTags
                        visitId={row.visit_id}
                        userTags={row.user_tags || []}
                        autoTags={row.auto_tags || row.tags || []}
                        onTagsUpdated={onVisitTagsUpdated}
                      />
                    </Stack>
                  </TableCell>

                  <TableCell align="center" sx={{ ...DATA_CELL_SX, color: BRAND_BLUE }}>
                    {displayOrDash(state)}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...DATA_CELL_SX,
                      color: BRAND_BLUE,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                    title={country || "—"}
                  >
                    {displayOrDash(country)}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...DATA_CELL_SX,
                      color: BRAND_BLUE,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 240,
                    }}
                    title={sectorLabel || "—"}
                  >
                    {displayOrDash(sectorLabel)}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...DATA_CELL_SX,
                      color: BRAND_BLUE,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 320,
                    }}
                    title={naicsName || "—"}
                  >
                    {displayOrDash(naicsName)}
                  </TableCell>
                </TableRow>
              );
            })}

            {hiddenRowCount > 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{
                    py: 1.5,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "rgba(0,51,108,0.45)",
                    borderTop: "1px solid #dce4ef",
                    borderBottom: "none",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Showing {sortedRows.length.toLocaleString()} of {totalRowCount.toLocaleString()} — scroll to load more
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {renderFilterMenu()}
    </TableContainer>
  );
}

export default React.memo(WebsiteVisitsTable);
